/**
 * AuthProvider / useAuth — thin React wrapper over workosAuth.ts.
 *
 * Mounted only in workos mode (see main.tsx). It subscribes to the module-level
 * auth store and exposes the sign-in lifecycle to components. The session
 * lifecycle itself lives in workosAuth.ts (server-side confidential flow); the
 * OAuth code exchange happens on the API, so there is no client-side callback.
 */

import {
  createContext,
  useContext,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import {
  subscribeAuth,
  getAuthSnapshot,
  initAuth,
  signIn,
  signOut,
  type User,
} from './workosAuth'

export interface AuthContextValue {
  isLoading: boolean
  isAuthenticated: boolean
  user: User | null
  signIn: () => void
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const snapshot = useSyncExternalStore(subscribeAuth, getAuthSnapshot, getAuthSnapshot)

  useEffect(() => {
    void initAuth()
  }, [])

  const value: AuthContextValue = {
    isLoading: snapshot.status === 'loading',
    isAuthenticated: snapshot.status === 'authenticated',
    user: snapshot.user,
    signIn,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used inside an <AuthProvider>')
  }
  return ctx
}
